import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, CheckCircle2, AlertTriangle, LogOut, Zap, RefreshCw, Info } from "lucide-react";
import ClientPicker from "@/components/ClientPicker";
import { Card, Field, Row, inputCls } from "./_shared";
import { TestResultPanel } from "./EndpointsStep";

const CFG_FIELDS = [
    ["token_path", "Token path", "/api/v1/ai-agent/token", "POSTed with the client's key"],
    ["key_field", "Key field", "api_key", "JSON field carrying the key"],
    ["token_field", "Token field", "token", "where the token is in the answer (dotted path ok)"],
    ["expires_field", "Expiry field", "expires_in", "seconds the token is valid"],
    ["logout_path", "Logout path", "/api/v1/auth/logout", "called with the token when the task ends"],
    ["header_name", "Header", "Authorization", "carries the token on every call"],
    ["header_prefix", "Header prefix", "Bearer ", "written before the token"],
];

/**
 * Auth step for `sitecentric_client_key` connections: no secret slots — the key
 * of the client the run works for is exchanged for a session token that lives
 * exactly as long as the task, then is logged out. Shows readiness for a client,
 * the token / logout settings, open sessions and a "obtain token → probe → log
 * out" test.
 */
export default function SiteCentricAuthPanel({ conn, immutable, canEdit, canTest, onSaved }) {
    const [clientId, setClientId] = useState(conn.client_id);
    const [ready, setReady] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [err, setErr] = useState(null);
    const [testing, setTesting] = useState(false);
    const [testRun, setTestRun] = useState(null);
    const [edit, setEdit] = useState(false);
    const [form, setForm] = useState({ ...(conn.auth_config || {}) });
    const [saving, setSaving] = useState(false);

    const load = async (cid = clientId) => {
        setErr(null);
        try {
            const [r, s] = await Promise.all([
                api.get(`/admin/connections/${conn.id}/sitecentric`, { params: { client_id: cid } }),
                api.get(`/admin/connections/${conn.id}/sessions`),
            ]);
            setReady(r.data);
            setSessions(s.data?.items || []);
        } catch (e) {
            setErr(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };
    useEffect(() => { load(clientId); }, [clientId, conn.id, conn.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

    const test = async () => {
        setTesting(true); setTestRun(null);
        try {
            const { data } = await api.post(`/admin/connections/${conn.id}/test/base`, null, { params: { client_id: clientId } });
            setTestRun(data);
            if (data.success) toast.success(`Token obtained, base answered ${data.statusCode}, logged out`);
            else toast.warning(`Test failed — ${data.error?.code || data.error?.errorType || data.statusCode}`);
            onSaved({ silent: true });
            load(clientId);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setTesting(false);
        }
    };

    const logoutAll = async () => {
        if (!window.confirm("Log every open session of this connection out of SiteCentric?")) return;
        try {
            const { data } = await api.delete(`/admin/connections/${conn.id}/sessions`);
            toast.success(`${data.released} session${data.released === 1 ? "" : "s"} logged out`);
            load(clientId);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/admin/connections/${conn.id}`, { auth_config: form });
            toast.success("SiteCentric auth settings saved");
            setEdit(false);
            onSaved();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const cfg = ready?.config || conn.auth_config || {};
    return (
        <div className="space-y-3" data-testid="sitecentric-auth-panel">
            <div className="px-3 py-2 rounded-md border border-sky-300/20 bg-sky-300/[0.04] text-[11px] text-sky-100/90 flex items-start gap-2">
                <KeyRound className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <div>
                    No secret to store here. When a flow, agent or scheduled task calls this connection, the <b>API key of the client the run works for</b>
                    {" "}(its designated SiteCentric key, else its newest active key) is sent to <span className="font-mono">{cfg.token_path}</span>; the session token
                    SiteCentric answers with is kept for that one task, reused by every endpoint call in it, then forgotten and
                    {" "}<span className="font-mono">{cfg.logout_method} {cfg.logout_path}</span> is called to end the session.
                </div>
            </div>

            {err && <div className="text-xs text-rose-200 border border-rose-300/30 bg-rose-300/10 rounded px-3 py-2" data-testid="sc-auth-error">{err}</div>}

            <div className="grid md:grid-cols-2 gap-3">
                <Card title="Readiness">
                    {conn.is_common && (
                        <ClientPicker value={clientId} onChange={setClientId} label="Check for client" includeSuperdomain testId="sc-auth-client" />
                    )}
                    {ready ? (
                        <div className="space-y-1.5" data-testid="sc-auth-readiness">
                            <Row k="Client" v={<span className="font-mono">{ready.client_id}</span>} />
                            <Row k="Status" v={ready.ready
                                ? <span className="text-emerald-200 inline-flex items-center gap-1" data-testid="sc-auth-ready"><CheckCircle2 className="w-3 h-3" /> key available</span>
                                : <span className="text-amber-200 inline-flex items-center gap-1" data-testid="sc-auth-not-ready"><AlertTriangle className="w-3 h-3" /> {ready.reason}</span>} />
                            {ready.key && <Row k="Key sent" v={<span className="font-mono">{ready.key.prefix}… · {ready.key.name || "unnamed"} · {ready.key.source}</span>} />}
                            {!ready.ready && ready.hint && <div className="text-[10px] text-amber-100/80 pt-1" data-testid="sc-auth-hint">{ready.hint} <Link to="/clients" className="underline">Clients</Link></div>}
                            <Row k="Token URL" v={<span className="font-mono break-all text-[10px]">{ready.token_url}</span>} />
                            <Row k="Logout URL" v={<span className="font-mono break-all text-[10px]">{ready.logout_url}</span>} />
                        </div>
                    ) : <div className="text-white/40">Loading…</div>}
                </Card>
                <Card title="Open sessions">
                    <Row k="Sessions kept right now" v={<span className="font-mono" data-testid="sc-auth-session-count">{sessions.length}</span>} />
                    {sessions.slice(0, 5).map((s) => (
                        <div key={`${s.client_id}-${s.scope_id}`} className="text-[10px] font-mono text-white/55 flex justify-between gap-2" data-testid={`sc-auth-session-${s.scope_id}`}>
                            <span>{s.client_id} · {s.scope_id?.slice(0, 12)}</span>
                            <span>until {(s.expires_at || "").slice(11, 16)} UTC</span>
                        </div>
                    ))}
                    <div className="text-[10px] text-white/40 pt-1">A session normally exists only while a task runs; stale ones are swept and logged out automatically.</div>
                    <div className="flex gap-2 pt-2 flex-wrap">
                        {canTest && (
                            <button onClick={test} disabled={testing || !ready?.ready} data-testid="sc-auth-test"
                                className="px-3 py-1.5 text-xs border border-emerald-300/30 text-emerald-200 rounded-md hover:bg-emerald-300/10 disabled:opacity-40 inline-flex items-center gap-1.5">
                                <Zap className="w-3 h-3" /> {testing ? "Running…" : "Obtain token, probe, log out"}
                            </button>
                        )}
                        {canTest && sessions.length > 0 && (
                            <button onClick={logoutAll} data-testid="sc-auth-logout-all"
                                className="px-3 py-1.5 text-xs border border-amber-300/30 text-amber-200 rounded-md hover:bg-amber-300/10 inline-flex items-center gap-1.5">
                                <LogOut className="w-3 h-3" /> Log out all
                            </button>
                        )}
                        <button onClick={() => load(clientId)} className="px-2 py-1.5 text-xs border border-white/15 rounded-md hover:bg-white/5" data-testid="sc-auth-refresh">
                            <RefreshCw className="w-3 h-3" />
                        </button>
                    </div>
                </Card>
            </div>

            {testRun && (
                <div className="space-y-2" data-testid="sc-auth-test-result">
                    {testRun.sitecentric && (
                        <div className="text-[11px] flex flex-wrap gap-3 px-3 py-2 rounded-md border border-white/10 bg-white/[0.02]">
                            <span className={testRun.sitecentric.tokenObtained ? "text-emerald-200" : "text-rose-200"}>
                                token {testRun.sitecentric.tokenObtained ? `obtained · valid ${Math.round((testRun.sitecentric.expiresIn || 0) / 60)} min` : `not obtained (${testRun.sitecentric.code})`}
                            </span>
                            {testRun.sitecentric.logout && (
                                <span className={testRun.sitecentric.logout.ok ? "text-emerald-200" : "text-amber-200"}>
                                    logout {testRun.sitecentric.logout.ok ? `ok (${testRun.sitecentric.logout.status})` : testRun.sitecentric.logout.error}
                                </span>
                            )}
                            {testRun.error?.hint && <span className="text-white/60">{testRun.error.hint}</span>}
                        </div>
                    )}
                    <TestResultPanel run={{ endpoint: { method: "GET", relative_path: conn.base_url }, env: testRun }} onClose={() => setTestRun(null)} />
                </div>
            )}

            <div className="border border-white/10 rounded-md p-3 space-y-2">
                <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-wider text-white/40">Token exchange settings</div>
                    {!immutable && canEdit && !edit && (
                        <button onClick={() => { setForm({ ...cfg }); setEdit(true); }} data-testid="sc-auth-edit"
                            className="px-2.5 py-1 text-[11px] border border-white/15 rounded-md hover:bg-white/5">Edit</button>
                    )}
                </div>
                {!edit ? (
                    <div className="grid md:grid-cols-2 gap-x-6 gap-y-1 text-xs" data-testid="sc-auth-config">
                        {CFG_FIELDS.map(([k, label]) => <Row key={k} k={label} v={<span className="font-mono">{String(cfg[k] ?? "")}</span>} />)}
                        <Row k="Logout method" v={<span className="font-mono">{cfg.logout_method}</span>} />
                        <Row k="Assumed lifetime" v={`${cfg.default_ttl_s} s`} />
                    </div>
                ) : (
                    <div className="space-y-3" data-testid="sc-auth-config-form">
                        <div className="grid md:grid-cols-2 gap-3">
                            {CFG_FIELDS.map(([k, label, ph, hint]) => (
                                <Field key={k} label={label} hint={hint}>
                                    <input value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph}
                                        data-testid={`sc-auth-f-${k}`} className={`${inputCls} font-mono`} />
                                </Field>
                            ))}
                            <Field label="Logout method">
                                <select value={form.logout_method || "POST"} onChange={(e) => setForm({ ...form, logout_method: e.target.value })}
                                    data-testid="sc-auth-f-logout_method" className={inputCls}>
                                    {["POST", "GET", "DELETE"].map((m) => <option key={m} value={m}>{m}</option>)}
                                </select>
                            </Field>
                            <Field label="Assumed lifetime (s)" hint="used when the answer has no expiry">
                                <input type="number" min={60} max={86400} value={form.default_ttl_s ?? 3600}
                                    onChange={(e) => setForm({ ...form, default_ttl_s: Number(e.target.value) })}
                                    data-testid="sc-auth-f-default_ttl_s" className={`${inputCls} font-mono`} />
                            </Field>
                        </div>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setEdit(false)} className="px-3 py-1.5 text-xs border border-white/15 rounded-md hover:bg-white/5">Cancel</button>
                            <button onClick={save} disabled={saving} data-testid="sc-auth-save"
                                className="px-3 py-1.5 text-xs bg-white text-black rounded-md disabled:opacity-40">{saving ? "Saving…" : "Save settings"}</button>
                        </div>
                    </div>
                )}
            </div>

            <div className="text-[10px] text-white/40 inline-flex items-center gap-1.5">
                <Info className="w-3 h-3" /> Exchanges and logouts are listed under Clients → client → SiteCentric agent access → audit (keys and tokens never appear).
            </div>
        </div>
    );
}
